import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Shield, Award, Users, CheckCircle, Star, Flame, ArrowRight, Phone, Clock, MapPin, AlertTriangle, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import EngineerMap from '../components/EngineerMap';
import axios from 'axios';
import { trackPageView } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [postcode, setPostcode] = useState('');
  const [searchError, setSearchError] = useState('');
  const [searchCount, setSearchCount] = useState(0);
  const [engineerCount, setEngineerCount] = useState(0);
  const [latestEngineer, setLatestEngineer] = useState(null);
  const [suggestions, setSuggestions] = useState({ engineers: [], count: 0, radius: null });
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();

  // Debounced live search — fetch suggestions as the user types
  useEffect(() => {
    const trimmed = postcode.trim();
    if (trimmed.length < 3) {
      setSuggestions({ engineers: [], count: 0, radius: null });
      setSuggestionsLoading(false);
      return;
    }
    setSuggestionsLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/engineers/search`, {
          params: { postcode: trimmed },
          timeout: 15000,
          signal: controller.signal
        });
        if (res.data?.success) {
          setSuggestions({
            engineers: res.data.engineers || [],
            count: res.data.count || 0,
            radius: res.data.searchRadius || null
          });
        } else {
          setSuggestions({ engineers: [], count: 0, radius: null });
        }
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          setSuggestions({ engineers: [], count: 0, radius: null });
        }
      } finally {
        setSuggestionsLoading(false);
      }
    }, 350);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [postcode]);

  useEffect(() => {
    trackPageView('home');
    // Fetch counters with retry-once so a single slow response doesn't
    // leave the homepage counter blank.
    const fetchWithRetry = async (url, attempts = 2) => {
      let lastErr;
      for (let i = 0; i < attempts; i++) {
        try {
          const res = await axios.get(url, { timeout: 15000 });
          return res.data;
        } catch (err) {
          lastErr = err;
          if (i < attempts - 1) {
            await new Promise(r => setTimeout(r, 800));
          }
        }
      }
      throw lastErr;
    };

    fetchWithRetry(`${API}/search-count`)
      .then(data => setSearchCount(data?.count || 0))
      .catch(err => console.warn('search-count failed:', err?.message));

    fetchWithRetry(`${API}/engineers-count`)
      .then(data => {
        setEngineerCount(data?.count || 0);
        setLatestEngineer(data?.latest || null);
      })
      .catch(err => console.warn('engineers-count failed:', err?.message));
  }, []);

  const validatePostcode = (input) => {
    const trimmed = input.trim().toUpperCase();
    if (!trimmed) return { valid: false, error: '' };
    if (/^[A-Z\s]{4,}$/.test(trimmed) && !/^[A-Z]{1,2}\s*$/.test(trimmed)) {
      return { valid: false, error: `"${trimmed}" looks like a town name. Please enter a postcode instead, e.g. SW1A 1AA or CM11` };
    }
    if (trimmed.length < 2) {
      return { valid: false, error: 'Please enter at least the first part of your postcode, e.g. CM11 or SW1A' };
    }
    if (!/^[A-Z]/.test(trimmed)) {
      return { valid: false, error: 'UK postcodes start with a letter. Please try again, e.g. SW1A 1AA' };
    }
    if (!/\d/.test(trimmed)) {
      return { valid: false, error: 'That doesn\'t look like a valid postcode. Try entering yours, e.g. CM11 2FA' };
    }
    if (trimmed.replace(/\s/g, '').length > 8) {
      return { valid: false, error: 'That postcode seems too long. UK postcodes are 5-7 characters, e.g. SW1A 1AA' };
    }
    return { valid: true, error: '' };
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchError('');
    const result = validatePostcode(postcode);
    if (!result.valid) {
      setSearchError(result.error);
      return;
    }
    navigate(`/find-engineer?postcode=${encodeURIComponent(postcode.trim())}`);
  };

  return (
    <div className="home-redesign">
      {/* Slim Gas Emergency Banner - Top */}
      <div className="gas-emergency-slim">
        <AlertTriangle size={16} />
        <span><strong>Smell gas?</strong> Call <a href="tel:0800111999">0800 111 999</a> immediately</span>
      </div>

      {/* Hero Section */}
      <section className="hero-premium">
        <div className="hero-bg-layer">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/c44e0078-dad0-4daf-ae5e-afa063e2af0b/images/3f046d0b8aff1f8778155cced6d060d061d258585797615673f2780eff1c13c7.png" 
            alt="Gas flame" 
            className="hero-bg-image"
          />
          <div className="hero-overlay"></div>
        </div>
        
        <div className="hero-content-wrapper">
          <div className="hero-badge">
            <Shield size={16} />
            <span>UK&apos;s Premium Gas Engineer Directory</span>
          </div>

          {/* Live engineer counter — split-flap style */}
          {engineerCount > 0 && (
            <div className="hero-live-counter" data-testid="hero-live-counter">
              <span className="hero-live-dot" />
              <span className="hero-live-label">Engineers Listed</span>
              <span className="hero-live-digits">
                {String(engineerCount).padStart(3, '0').split('').map((d, i) => (
                  <span key={i} className="hero-live-digit">{d}</span>
                ))}
              </span>
              <span className="hero-live-counting">&amp; counting</span>
            </div>
          )}
          
          <h1 className="hero-headline">
            FIND TRUSTED<br />
            <span className="text-highlight">GAS SAFE</span> ENGINEERS
          </h1>
          
          <p className="hero-subheadline">
            Every engineer verified, insured, and vetted. No cowboys. No call-outs gone wrong. 
            Just qualified professionals you can trust with your home.
          </p>

          {/* Search Box */}
          <form onSubmit={handleSearch} className="hero-search-form" data-testid="hero-search-form">
            <div className="search-input-wrapper" style={{ position: 'relative' }}>
              <MapPin className="search-icon" size={22} />
              <input
                type="text"
                placeholder="Enter your postcode..."
                value={postcode}
                onChange={(e) => { setPostcode(e.target.value); setSearchError(''); setShowSuggestions(true); }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="search-input"
                data-testid="hero-postcode-input"
                autoComplete="off"
              />
              <Button type="submit" className="search-btn" data-testid="hero-search-btn">
                <Search size={20} />
                <span>Find Engineers</span>
              </Button>

              {/* Live search suggestions */}
              {showSuggestions && postcode.trim().length >= 3 && (
                <div className="postcode-suggestions" data-testid="postcode-suggestions">
                  {suggestionsLoading && suggestions.engineers.length === 0 && (
                    <div className="postcode-suggestion-empty" data-testid="suggestion-loading">Searching…</div>
                  )}
                  {!suggestionsLoading && suggestions.engineers.length === 0 && (
                    <div className="postcode-suggestion-empty" data-testid="suggestion-empty">
                      No engineers found in this area yet. Hit &quot;Find Engineers&quot; to log your postcode — we&apos;ll be in touch when one signs up nearby.
                    </div>
                  )}
                  {suggestions.engineers.slice(0, 5).map((eng) => (
                    <button
                      type="button"
                      key={eng.id}
                      className="postcode-suggestion-item"
                      data-testid={`suggestion-item-${eng.id}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => navigate(`/find-engineer?postcode=${encodeURIComponent(postcode.trim())}`)}
                    >
                      <div className="suggestion-name">
                        <span className="suggestion-primary">{eng.businessName || eng.fullName || 'Gas Safe Engineer'}</span>
                        {eng.fullName && eng.businessName && (
                          <span className="suggestion-secondary">{eng.fullName}</span>
                        )}
                      </div>
                      <div className="suggestion-meta">
                        <MapPin size={12} />
                        <span>{eng.businessPostcode || (eng.postcodes && eng.postcodes[0]) || ''}</span>
                        {eng.distance != null && (
                          <span className="suggestion-distance">{eng.distance.toFixed(1)} mi</span>
                        )}
                      </div>
                    </button>
                  ))}
                  {suggestions.count > 0 && (
                    <button
                      type="button"
                      className="postcode-suggestion-more"
                      data-testid="suggestion-see-all"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => navigate(`/find-engineer?postcode=${encodeURIComponent(postcode.trim())}`)}
                    >
                      See all {suggestions.count} result{suggestions.count === 1 ? '' : 's'}
                      <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </form>

          {searchError && (
            <div className="find-search-error" data-testid="hero-search-error">
              <AlertTriangle size={16} />
              <span>{searchError}</span>
            </div>
          )}

          {/* Trust Stats */}
          <div className="hero-stats">
            <div className="stat-item">
              <Shield className="stat-icon" size={20} />
              <span>Gas Safe Verified</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <CheckCircle className="stat-icon" size={20} />
              <span>Insurance Checked</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <Star className="stat-icon" size={20} />
              <span>Customer Reviewed</span>
            </div>
          </div>

          {searchCount >= 10 && (
            <div className="hero-search-count" data-testid="search-count-stat">
              <TrendingUp size={15} />
              <span><strong>{searchCount}+</strong> homeowner searches and counting</span>
            </div>
          )}
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <span>Scroll to learn more</span>
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="trust-bar">
        <div className="trust-bar-inner">
          <div className="trust-item-bar gas-safe-badge">
            <span className="gas-safe-text">Gas Safe<br/>Registered</span>
          </div>
          <div className="trust-item-bar">
            <span className="trust-text">✓ All Engineers Verified</span>
          </div>
          <div className="trust-item-bar">
            <span className="trust-text">✓ Public Liability Insured</span>
          </div>
          <div className="trust-item-bar">
            <span className="trust-text">✓ No Bidding Wars</span>
          </div>
          <div className="trust-item-bar">
            <span className="trust-text">✓ Direct Contact</span>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-how-it-works">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">Simple Process</span>
            <h2 className="section-title-premium">HOW IT WORKS</h2>
            <p className="section-desc">Find your trusted gas engineer in three easy steps</p>
          </div>

          <div className="steps-container">
            <div className="step-card-premium" data-testid="step-1">
              <div className="step-number-badge">01</div>
              <div className="step-icon-wrapper">
                <Search size={32} />
              </div>
              <h3 className="step-title-premium">Enter Your Postcode</h3>
              <p className="step-desc-premium">
                Search for Gas Safe registered engineers covering your local area
              </p>
            </div>

            <div className="step-connector">
              <ArrowRight size={24} />
            </div>

            <div className="step-card-premium" data-testid="step-2">
              <div className="step-number-badge">02</div>
              <div className="step-icon-wrapper">
                <Shield size={32} />
              </div>
              <h3 className="step-title-premium">View Vetted Pros</h3>
              <p className="step-desc-premium">
                See verified engineers with reviews, services, and contact details
              </p>
            </div>

            <div className="step-connector">
              <ArrowRight size={24} />
            </div>

            <div className="step-card-premium" data-testid="step-3">
              <div className="step-number-badge">03</div>
              <div className="step-icon-wrapper">
                <Phone size={32} />
              </div>
              <h3 className="step-title-premium">Contact Directly</h3>
              <p className="step-desc-premium">
                Call or message the engineer directly. No middlemen, no lead fees
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bento Grid Features */}
      <section className="section-features-bento">
        <div className="container-premium">
          <div className="section-header-premium">
            <span className="section-label">Why Choose Us</span>
            <h2 className="section-title-premium">NOT JUST ANOTHER DIRECTORY</h2>
            <p className="section-desc">The Gas Guide is different. Here's why.</p>
          </div>

          <div className="bento-grid">
            {/* Large Card - Safety First */}
            <div className="bento-card bento-large" data-testid="feature-safety">
              <div className="bento-image-wrapper">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/c44e0078-dad0-4daf-ae5e-afa063e2af0b/images/5698aeaf96279c29518dbbb48f4b90306b5dfde1f599848324c6e786b27315e8.png" 
                  alt="Modern gas boiler installation" 
                  className="bento-image"
                />
                <div className="bento-image-overlay"></div>
              </div>
              <div className="bento-content">
                <Shield className="bento-icon" size={28} />
                <h3 className="bento-title">THOROUGHLY VETTED</h3>
                <p className="bento-desc">
                  Every engineer is verified for Gas Safe registration, public liability insurance, 
                  and years of experience before being listed. Your safety is our priority.
                </p>
              </div>
            </div>

            {/* Medium Card - Quality */}
            <div className="bento-card bento-medium" data-testid="feature-quality">
              <div className="bento-content">
                <Award className="bento-icon" size={28} />
                <h3 className="bento-title">QUALITY OVER QUANTITY</h3>
                <p className="bento-desc">
                  Every engineer is Gas Safe verified before being listed. Ranked by customer reviews, not who pays more.
                </p>
              </div>
            </div>

            {/* Medium Card - No Lead Selling */}
            <div className="bento-card bento-medium bento-dark" data-testid="feature-no-leads">
              <div className="bento-content">
                <Users className="bento-icon" size={28} />
                <h3 className="bento-title">NO LEAD SELLING</h3>
                <p className="bento-desc">
                  Engineers don&apos;t compete for your job. No shared leads, no bidding wars.
                </p>
              </div>
            </div>

            {/* Small Cards Row */}
            <div className="bento-card bento-small" data-testid="feature-monitoring">
              <CheckCircle className="bento-icon-sm" size={24} />
              <h4 className="bento-title-sm">Ongoing Monitoring</h4>
              <p className="bento-desc-sm">Regular checks ensure standards are maintained</p>
            </div>

            <div className="bento-card bento-small" data-testid="feature-ranking">
              <Star className="bento-icon-sm" size={24} />
              <h4 className="bento-title-sm">Fair Rankings</h4>
              <p className="bento-desc-sm">Reviews determine position, not payment</p>
            </div>

            <div className="bento-card bento-small bento-highlight" data-testid="feature-founding">
              <Flame className="bento-icon-sm" size={24} />
              <h4 className="bento-title-sm">Founding Members</h4>
              <p className="bento-desc-sm">All early engineers get the founding badge — and keep it for life</p>
            </div>
          </div>
        </div>
      </section>

      {/* Public Engineer Counter + Map */}
      <section className="section-engineer-map" data-testid="public-engineer-map-section">
        <div className="container-premium">
          <div className="map-counter-header">
            <span className="section-label">Live on the directory</span>
            <h2 className="section-title-premium">
              <span className="counter-number" data-testid="engineer-counter">{engineerCount.toLocaleString()}</span>
              <span className="counter-suffix"> {engineerCount === 1 ? 'engineer' : 'engineers'} currently listed</span>
              <span className="counter-counting">&amp; counting…</span>
            </h2>
            <p className="counter-subtext">Every one of them Gas Safe verified by us. Browse the map to see where they cover.</p>
            {latestEngineer && (
              <div className="latest-engineer-ticker" data-testid="latest-engineer-ticker">
                <span className="ticker-dot" />
                Latest engineer joined <strong>{latestEngineer.when}</strong> in <strong>{latestEngineer.where}</strong>
              </div>
            )}
          </div>
          <div className="public-map-wrap">
            <EngineerMap />
          </div>
        </div>
      </section>

      {/* For Engineers Section */}
      <section className="section-for-engineers">
        <div className="container-premium">
          <div className="engineers-split">
            <div className="engineers-content">
              <span className="section-label light">For Gas Engineers</span>
              <h2 className="section-title-premium light">GROW YOUR BUSINESS</h2>
              <p className="engineers-desc">
                Join The Gas Guide and connect directly with homeowners in your area. 
                No lead fees, no bidding against other engineers. Just quality customers 
                who found you because of your reputation.
              </p>
              <ul className="engineers-benefits">
                <li><CheckCircle size={20} /> Direct customer contact</li>
                <li><CheckCircle size={20} /> No per-lead charges</li>
                <li><CheckCircle size={20} /> Build your reputation with reviews</li>
                <li><CheckCircle size={20} /> Free Founding Member badge for joining now</li>
              </ul>
              <Link to="/for-engineers">
                <Button className="btn-engineer-cta" data-testid="engineer-signup-btn">
                  Apply Now
                  <ArrowRight size={18} />
                </Button>
              </Link>
            </div>
            <div className="engineers-image-wrapper">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/c44e0078-dad0-4daf-ae5e-afa063e2af0b/images/28523639033f2b8dfa03da195ae7354c3059bb0f585f16e673f298185cb1079d.png" 
                alt="Professional gas boiler in home" 
                className="engineers-image"
              />
              <div className="engineers-badge-float">
                <Clock size={20} />
                <span>100% free to join</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-final-cta">
        <div className="container-premium">
          <div className="cta-box">
            <h2 className="cta-headline">READY TO FIND YOUR ENGINEER?</h2>
            <p className="cta-subline">
              Join thousands of UK homeowners who've found reliable, vetted Gas Safe professionals through The Gas Guide.
            </p>
            <form onSubmit={handleSearch} className="cta-search-form">
              <input
                type="text"
                placeholder="Enter your postcode..."
                value={postcode}
                onChange={(e) => { setPostcode(e.target.value); setSearchError(''); }}
                className="cta-search-input"
                data-testid="cta-postcode-input"
              />
              <Button type="submit" className="cta-search-btn" data-testid="cta-search-btn">
                <Search size={20} />
                Search Now
              </Button>
            </form>
            {searchError && (
              <div className="find-search-error" data-testid="cta-search-error" style={{ marginTop: '12px' }}>
                <AlertTriangle size={16} />
                <span>{searchError}</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
